<script lang="ts" setup>
import type { InputCreateAddress, IonSearchbar } from '#build/components';
import { ErrorWithBreadcrumb } from '~/errors';

const emits = defineEmits<{
  select: [address: AddressDAWA]
}>();

defineExpose({ open });
const hotkeyManager = useHotkeyManager();
const { t } = useEkI18n();
const { focusOnEnter } = useWebInputManager();
const platformStore = usePlatformStore();
const isOpen = ref<boolean>(false);
const searchbar = useTemplateRef('searchbar');
const customerAddressDawaModal = useTemplateRef('customerAddressDawaModal');
const createAddressInput = ref<InstanceType<typeof InputCreateAddress>>();
const query = ref<string>('');
const proposals = ref<InternalAddress[]>([]);
const addressFocus = ref<string>('addressFocus');

watch(query, n => search(n));

function open() {
  isOpen.value = true;

  if (platformStore.isWeb) {
    hotkeyManager.registerModalHotkeys({
      [Hotkeys.Escape]: () => reset()
    }, customerAddressDawaModal.value?.$el);
  }
}

function reset() {
  isOpen.value = false;
  query.value = '';
  proposals.value = [];

  if (platformStore.isWeb) {
    hotkeyManager.clearModalHotkeys(customerAddressDawaModal.value?.$el);
  }
}

function select(address: AddressDAWA) {
  emits('select', address);
  reset();
}

async function search(query: string) {
  if (query === '') {
    return []; 
  }

  proposals.value = await $fetch<InternalAddress[]>(`https://api.dataforsyningen.dk/autocomplete?q=${query}&fuzzy=true`);
  const exactMatch = proposals.value.find(p => p.tekst === query && p.type === 'adresse');

  if (exactMatch) {
    select(map(exactMatch));
  }

  if (proposals.value.length === 1 && proposals.value[0].type === 'adresse') {
    select(map(proposals.value[0]));
  }
}

function handleCreateAddress(address: CreateAssociatedAddressResponseDTO) {
  select({
    Id: address.ID,
    StreetName: address.Address,
    Unit: address.Address2,
    City: address.ZipCode.CityName,
    Zip: address.ZipCode.Code
  });
}

async function handleItemClick(proposal: InternalAddress) {
  if (query.value === proposal.tekst && proposal.type === 'adresse') {
    select(map(proposals.value[0]));
  }

  query.value = proposal.tekst;
  const input = await searchbar.value!.$el.getInputElement();
  input.focus();
  input.setSelectionRange(proposal.caretpos, proposal.caretpos);
}

function map(internalAddress: InternalAddress): AddressDAWA {
  if (!internalAddress.data.postnrnavn || !internalAddress.data.postnr) {
    throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.AddressDawa, message: t('error.missingCityOrZipCode') });
  }

  return {
    City: internalAddress.data.postnrnavn,
    StreetName: internalAddress.data.vejnavn ?? '',
    StreetNumber: internalAddress.data.husnr ?? '',
    Unit: [internalAddress.data.etage, internalAddress.data.dør].filter(Boolean).join('. ') || '',
    Zip: internalAddress.data.postnr ?? ''
  };
}

function onEnter() {
  if (!platformStore.isWeb) {
    return; 
  }

  focusOnEnter('.' + addressFocus.value);
}
</script>

<template>
  <Modal
    ref="customerAddressDawaModal"
    :open="isOpen"
    :title="t('titles.searchForAddress')"
    animation-direction="vertical"
    :close-override="reset"
    @did-present="searchbar?.$el.setFocus();"
    @handle-dismiss="reset"
  >
    <template #headerButtons>
      <IonButton @click="createAddressInput?.open()">
        <IonIcon
          slot="icon-only"
          class="fa-light fa-fw fa-location-plus"
        />
      </IonButton>
    </template>
    <template #subheader>
      <IonSearchbar
        ref="searchbar"
        v-model="query"
        :placeholder="t('search.address')"
        :debounce="500"
        @keyup.enter="onEnter"
      />
    </template>
    <IonContent>
      <IonList>
        <IonItem
          v-for="item in proposals"
          :key="item.id"
          :class="addressFocus"
          lines="full"
          button
          @click="handleItemClick(item)"
        >
          {{ item.forslagstekst }}
        </IonItem>
      </IonList>
    </IonContent>
  </Modal>

  <InputCreateAddress
    ref="createAddressInput"
    @created="handleCreateAddress"
  />
</template>
